import { Component, ViewChild } from '@angular/core';
import { IonicPage, ModalController, NavController, NavParams, Platform, ToastController } from 'ionic-angular';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { LockedDayService } from '../../../services/locked-day/locked-day.service';
import { DateService } from '../../../services/date/date.service';
import { DateParserService } from '../../../services/daily-log/daily-log-time/date-parser-service';
import {
  DailyLegLoad,
  DailyLegLoadFrequencyEditor,
  DailyLegLoadFrequencyFilters, DailyLegLoadStandaloneEditor,
} from '../../../models/daily-leg-loads/daily-leg-load.models';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { AppConfig } from '../../../app/app.config';
import { DateTime } from 'luxon';
import { DailyLegLoadFrequencyService } from '../../../services/daily-leg-loads/daily-leg-load-frequency.service';
import { State } from '@progress/kendo-data-query';
import { ConfirmationModalComponent } from '../../daily-log/confirmation-modal/confirmation-modal.component';
import {
  DailyLegLoadsAddFrequencyComponent
} from './components/daily-leg-load-add-frequency/daily-leg-loads-add-frequency';
import { NavHelperService } from '../../../services/nav/nav-helper.service';
import { SelectEvent } from '@progress/kendo-angular-layout';
import { DailyLegLoadStandaloneService } from '../../../services/daily-leg-loads/daily-leg-load-standalone.service';
import {
  DailyLegLoadsAddStandaloneReadingComponent
} from './components/daily-leg-load-add-standalone-reading/daily-leg-loads-add-standalone-reading';
import { SupportContextKey } from '../../../models/support/support.models';
import { SupportContextService } from '../../../services/support/support-context.service';

@IonicPage()
@Component({
  selector: 'page-daily-leg-loads-manage-readings',
  templateUrl: 'daily-leg-load-manage-readings.html',
})
export class DailyLegLoadsManageReadingsPage {
  config = AppConfig.settings;
  loading: boolean;

  dailyLogDate: PtLuxonDate = new PtLuxonDate();
  mask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];
  currentEditor: DailyLegLoadFrequencyEditor;
  timeOfChange: string;
  isWeb: boolean;
  selectedTabIndex: number;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    private toastCtrl: ToastController,
    private lockedDayService: LockedDayService,
    private dateService: DateService,
    private dateParserService: DateParserService,
    private dailyLogService: DailyLogService,
    private dailyLegLoadFrequencyService: DailyLegLoadFrequencyService,
    private dailyLegLoadStandaloneService: DailyLegLoadStandaloneService,
    private platform: Platform,
    private modalController: ModalController,
    private navHelperService: NavHelperService,
    private supportContextService: SupportContextService,
  ) {
    this.selectedTabIndex = 0;
  }

  async ngOnInit() {
    this.supportContextService.setComponentContext(SupportContextKey.LegLoads, 'Manage Leg Load Readings', 'DailyLegLoadsManageReadingsPage');

    this.loading = true;
    this.isWeb = this.platform.is('core');

    // update daily log date with date from dailyLogService
    this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();

    // create adjusted date with day part of the daily log date, but the time part from the local machine time
    const newDate = DateTime.local();
    const adjustedDate = this.dailyLogDate.dateTime.set({
      year: this.dailyLogDate.dateTime.year,
      month: this.dailyLogDate.dateTime.month,
      day: this.dailyLogDate.dateTime.day,
      hour: newDate.hour,
      minute: newDate.minute,
      second: 0,
      millisecond: 0
    });

    this.timeOfChange = this.dateService.setTimeStringFromDateTime(adjustedDate);

    await this.updateEditor(adjustedDate);
  }

  async updateEditor(date?: DateTime) {
    this.currentEditor = await this.getEditor(date);

    if (this.currentEditor == null) {
      this.currentEditor = new DailyLegLoadFrequencyEditor();
      this.currentEditor.frequencySeconds = 3600;
      this.currentEditor.frequencyShow = '01:00';
      this.currentEditor.effectiveFromDate = this.dailyLogDate;
    }
  }

  async getEditor(date?: DateTime): Promise<DailyLegLoadFrequencyEditor> {
    const frequencyFilters = new DailyLegLoadFrequencyFilters();
    frequencyFilters.date = date || this.dailyLogDate.getDate();

    const editor = await this.dailyLegLoadFrequencyService.getDailyLegLoadFrequencyEditor(frequencyFilters);

    if (editor && editor.effectiveFromDate) {
      editor.effectiveFromDateShow = this.dateService.setFullTimeString(editor.effectiveFromDate.dateTime);
    }

    this.loading = false;

    return editor;
  }

  presentSaveToast(): Promise<any> {
    const toast = this.toastCtrl.create({
      message: 'Leg Load Frequency Updated',
      duration: 3000,
      position: 'bottom'
    });

    return toast.present();
  }

  openDeleteFrequencyConfirmationModel(row: DailyLegLoadFrequencyEditor) {
    const confirmationModal = this.modalController.create(ConfirmationModalComponent, {
      title: 'Delete Confirmation',
      comment: `EF: ${row.effectiveFromDateShow} - Frequency: ${row.frequencyShow}`,
      message: 'Are you sure you want to remove this frequency change?',
      optionalMessage: 'This may result in rows being removed from the leg load log, any data already entered in these rows will be lost.',
      trueText: 'Confirm',
      falseText: 'Cancel',
      showButtons: true
    }, {cssClass: 'daily-log-modal-four-line'});

    confirmationModal.present();

    confirmationModal.onDidDismiss(async data => {
      if (data && data.choice) {
        await this.deleteFrequencyEditor(row);
      }
    })
  }

  openFrequencyChangeModal() {
    const modal = this.modalController.create(DailyLegLoadsAddFrequencyComponent, {
      isWeb: this.isWeb,
      dailyLogDate: this.dailyLogDate,
      currentEditor: this.currentEditor
    }, {
      cssClass: 'daily-leg-load-add-frequency'
    });

    modal.present();

    modal.onDidDismiss(async data => {
      if (data && data.choice) {
        // flag the leg load log to reload once the user goes back so the new frequency takes effect
        this.navHelperService.updateLegLoadLogRequiresReload(true);

        await this.ngOnInit();
      }
    })
  }

  async deleteFrequencyEditor(editor: DailyLegLoadFrequencyEditor) {
    // flag the leg load log to reload once the user goes back so the new frequency takes effect
    this.navHelperService.updateLegLoadLogRequiresReload(true);

    await this.dailyLegLoadFrequencyService.deleteDailyLegLoadFrequencyEditor(editor).then(() => {
      this.ngOnInit();
    });
  }

  openAddReadingModal() {
    const modal = this.modalController.create(DailyLegLoadsAddStandaloneReadingComponent, {
      isWeb: this.isWeb,
      dailyLogDate: this.dailyLogDate,
    }, {
      cssClass: 'daily-leg-load-add-reading'
    });

    modal.present();

    modal.onDidDismiss(async data => {
      if (data && data.choice) {
        // flag the leg load log to reload once the user goes back so the new frequency takes effect
        this.navHelperService.updateLegLoadLogRequiresReload(true);

        await this.ngOnInit();
        this.selectedTabIndex = 1;
      }
    })
  }

  openDeleteReadingConfirmationModal(row: DailyLegLoadStandaloneEditor) {
    const confirmationModal = this.modalController.create(ConfirmationModalComponent, {
      title: 'Delete Confirmation',
      comment: `Record: ${row.readingTimeShow}`,
      message: 'Are you sure you want to remove this reading?',
      optionalMessage: 'Any data already entered in this row will be lost.',
      trueText: 'Confirm',
      falseText: 'Cancel',
      showButtons: true
    }, {cssClass: 'daily-log-modal-four-line'});

    confirmationModal.present();

    confirmationModal.onDidDismiss(async data => {
      if (data && data.choice) {
        await this.deleteStandaloneReading(row);
      }
    })
  }

  private async deleteStandaloneReading(editor: DailyLegLoadStandaloneEditor) {
    // flag the leg load log to reload once the user goes back so the new frequency takes effect
    this.navHelperService.updateLegLoadLogRequiresReload(true);

    await this.dailyLegLoadStandaloneService.deleteDailyLegLoadStandaloneEditor(editor).then(() => {
      this.ngOnInit();
      this.selectedTabIndex = 1;
    });
  }
}



// WEBPACK FOOTER //
// ./src/pages/daily-leg-loads/daily-leg-load-manage-readings/daily-leg-load-manage-readings.ts